/**
 * utils/connectionUtils.ts
 * Network connection utilities (SSR-safe)
 *
 * Provides safe access to browser APIs that may not exist in all environments
 * (SSR, Node.js tests, Edge Functions, etc.)
 */

/**
 * Check if browser is online (SSR-safe)
 *
 * Safely accesses `navigator.onLine` with fallback for environments
 * where `navigator` is undefined (SSR, Node.js, Edge Functions).
 *
 * Assumption: If navigator is unavailable, assume online (optimistic).
 * This prevents blocking operations in SSR/test environments.
 *
 * @returns True if online, or if navigator is unavailable (SSR/test environment)
 *
 * @example
 * ```ts
 * if (!isOnline()) {
 *   return { success: false, reason: 'OFFLINE' };
 * }
 * ```
 *
 * @example Test mocking
 * ```ts
 * // Mock offline state
 * Object.defineProperty(navigator, 'onLine', {
 *   writable: true,
 *   value: false
 * });
 * expect(isOnline()).toBe(false);
 * ```
 */
export function isOnline(): boolean {
  // SSR/Node.js/Edge environment check
  if (typeof navigator === 'undefined') {
    return true; // Assume online in non-browser environments
  }

  // Browser environment
  return navigator.onLine;
}
